import { OpenInNew } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Link,
  Stack,
  SvgIconTypeMap,
  Typography,
} from "@mui/material";
import React, { PropsWithChildren } from "react";
import resumePdf from "./assets/projects/resume.pdf";
import { DetailsBasePage } from "./DetailsBasePage.tsx";
import { OverridableComponent } from "@mui/material/OverridableComponent";

export type Entry = {
  title: string;
  image?: string;
  subtitle: string;
  link?: string;
  LinkIcon?: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
  description: string;
  skills?: string[];
};

const allEntries: Entry[] = [
  {
    title: "Fullstack Engineer - Headway",
    subtitle: "New York, NY (Remote)\n2025 - ",
    link: "https://www.headway.co",
    description:
      "Building a new mental healthcare system that everyone can access. As a member of the patient growth team, lead initiatives to optimize the top of funnel experience for users seeking mental health care that's covered by insurance.",
    skills: [
      "React",
      "Python",
      "TypeScript",
      "Remix.js",
      "Search Engine Optimization (SEO)",
      "A/B Testing",
    ],
  },
  {
    title: "Frontend Engineer - Terratrue",
    subtitle: "San Francisco, CA (Remote)\n2022 - 2025 ",
    link: "https://www.terratrue.com",
    description:
      "Architect and lead frontend development for SaaS privacy-by-design platform. Collaborate closely with design and product teams to curate and deliver features for highly sophisticated privacy programs.",
    skills: [
      "React",
      "TypeScript",
      "Redux",
      "Formik",
      "Material UI",
      "Storybook",
      "Node.js",
      "GCP",
      "Cypress.io",
    ],
  },
  {
    title: "Founding Fullstack Engineer - RDY",
    subtitle: "Los Angeles, CA\n2021 - 2022",
    link: "https://www.instagram.com/rdyapp/",
    description:
      "Founding member of early-stage startup focused on supporting independent coffee shops. Designed and implemented suite of applications across web, tablet, and mobile interfaces, including leading a UI/UX rebrand in the early stage of RDY's visual identity.",
    skills: [
      "React",
      "React Native",
      "GraphQL",
      "Spring Boot",
      "TypeScript",
      "Kotlin",
      "mySQL",
      "Azure",
      "Material UI",
      "Auth0",
    ],
  },
  {
    title: "Fullstack Engineer - Zapshot",
    subtitle: "Tokyo, JP (Remote)\n2020 - 2021",
    link: "https://www.zapshot.me/",
    description:
      "Fullstack engineer for Tokyo-based social media startup driving innovation in video sharing and voice chat. Developed and maintained REST API for mobile clients, including authentication and push notifications. Designed and implemented web application for admininstators to moderate user content.",
    skills: ["Express.js", "Node.js", "mySQL", "AWS", "Vue.js"],
  },
];

export const WorkDetails = (props: PropsWithChildren) => {
  return (
    <DetailsBasePage title="Work">
      <Stack spacing={3} useFlexGap>
        {allEntries.map((entry, idx) => (
          <ResumeListItem entry={entry} key={entry.title} idx={idx} />
        ))}
        <Box display={"flex"} justifyContent={"center"}>
          <Button
            component={"a"}
            sx={(theme) => ({
              width: "50%",
              maxWidth: 250,
              [theme.breakpoints.down("md")]: {
                width: "100%",
              },
            })}
            variant="contained"
            size="large"
            endIcon={<OpenInNew />}
            // onClick={() => window.open(resumePdf, "_blank")}
            href={resumePdf}
            target="_blank"
          >
            View Full Resume
          </Button>
        </Box>
      </Stack>
    </DetailsBasePage>
  );
};

export const ResumeListItem = (props: { entry: Entry; idx?: number }) => {
  const {
    title,
    subtitle,
    link,
    description,
    skills = [],
    image,
    LinkIcon = OpenInNew,
  } = props.entry;

  return (
    <Link
      sx={(theme) => ({
        padding: 2,
        borderRadius: "8px",
        color: "text.primary",
        textDecoration: "none !important",
        transition: "background-color 0.8s",
        "&:hover": {
          backgroundColor: `${theme.palette.primary.main}33`,
          cursor: "pointer",
          "& img": {
            filter: "grayscale(0)",
          },
          "& svg": {
            opacity: 1,
          },
        },
        "& img": {
          transition: "0.8s",
          filter: "grayscale(0.8)",
        },
        "& svg": {
          transition: "0.8s",
        },
        position: "relative",
      })}
      href={link}
    >
      <LinkIcon
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "primary.main",
          opacity: 0,
        }}
      />
      <Typography fontWeight={"bold"} variant="h6" mb={1}>
        {title}
      </Typography>
      <Box
        sx={(theme) => ({
          display: "grid",
          gridTemplateColumns: "1fr 2fr",
          gap: 2,

          [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
          },
        })}
      >
        <Stack spacing={1}>
          <Typography whiteSpace={"pre-line"}>{subtitle}</Typography>
          {image && (
            <img
              src={image}
              alt={title}
              style={{
                width: "100%",
                height: "auto",
                borderRadius: "8px",
              }}
            />
          )}
        </Stack>
        <Stack spacing={2}>
          <Typography variant="body2" component={"em"}>
            {description}
          </Typography>
          <Stack direction={"row"} spacing={1} flexWrap={"wrap"} useFlexGap>
            {skills.map((skill) => (
              <Chip size="small" label={skill} variant="outlined" />
            ))}
          </Stack>
        </Stack>
      </Box>
    </Link>
  );
};
